import type {
    WebsocketDynamicArgumentsResult,
    WebsocketConnection,
    WebsocketConnectionFactory,
    WebsocketConnectionOptions
} from './types.ts';
import { getLogger } from '../../logger.ts';
import * as GatewayEncoders from '../encoders.ts';
import { IOGateway } from '../../../gateway';

const logger = getLogger('gateway.ws.common');

const ConnectionStates = Object.freeze({
    _initial:  -1,
    _disconnecting: -2,

    connecting: 0,
    connected:  1,
    closing:    2,
    closed:     3,
});
type ConnectionState = keyof typeof ConnectionStates;

type Opts<T> = {
    codec: GatewayEncoders.Codec<T, string>,
    maxEnqueued?: number,
    retry?: Retry,
    WebSocket: typeof WebSocket,
    dynamicArguments?: (url: string | URL, protocols?: string | readonly string[], closeEvent?: CloseEvent) => WebsocketDynamicArgumentsResult;
};

class InternalConnection<T> {
    constructor(url: string | URL,
                protocols: string | string[],
                opts: Opts<T>) {
        this.url = url;
        this.protocols = typeof protocols === 'string' ? protocols : [...protocols];
        this.dynamicArguments = opts.dynamicArguments;
        this.WebSocket = opts.WebSocket;
        this.codec = opts.codec;
        this.maxEnqueued = opts.maxEnqueued ?? -1;
        this.retry = opts.retry;
    }

    readonly dynamicArguments?: (url: string | URL, protocols?: string | readonly string[], closeEvent?: CloseEvent) => WebsocketDynamicArgumentsResult;
    readonly url: string | URL;
    readonly protocols: string | readonly string[];
    readonly WebSocket: typeof WebSocket;
    state: ConnectionState = '_initial';
    readonly codec: GatewayEncoders.Codec<T, string>;
    socket?: WebSocket & { _internalSocketId?: number };
    readonly maxEnqueued: number;
    readonly queue: string[] = [];
    readonly retry?: Retry;
    reconnectAttempt = 0;
    reconnectTimeout?: ReturnType<typeof setTimeout>;
    activeSocketId = 0;
}
const MIN_RECONNECT_DELAY = 50; // 50 ms
const DEFAULT_RETRY = {
    min: 1000 + (Math.random() * 4000), // between 1 and 5 seconds
    max: 60 * 1000, // 60 seconds
    factor: 1.3
};

function calculateReconnectDelay(attempt: number, retry?: Partial<typeof DEFAULT_RETRY>) {
    const min = Math.max(MIN_RECONNECT_DELAY, retry?.min ?? DEFAULT_RETRY.min);
    const max = Math.max(min, retry?.max ?? DEFAULT_RETRY.max);
    const factor = Math.max(1.0, retry?.factor ?? DEFAULT_RETRY.factor);
    const delay = Math.round(Math.min(max, min * Math.pow(factor, attempt)));
    return delay;
}

class WsConnection<T> implements WebsocketConnection<T> {
    readonly #internal: InternalConnection<T>;
    // current url with params
    #url: string;
    // current protocols
    #protocols?: string | string[];

    static readonly #DISCONNECT_REASON = 'interop.io:manual-disconnect';
    constructor(internal: InternalConnection<T>) {
        this.#internal = internal;
        this.#url = typeof internal.url === 'string' ? internal.url : internal.url.href;
    }

    get url() {
        return this.#url;
    }

    onConnect?: (url: string, protocol?: string) => void
    onReceive?: (msg: T) => void
    onError?: (e: Error) => void
    onDisconnect?: (code: number, reason: string, wasClean: boolean) => void
    onClose?: () => void

    #createAndInit(closeEvent?: CloseEvent) {
        const id = ++this.#internal.activeSocketId;
        if (logger.enabledFor('debug')) {
            logger.debug(`[${id}]: connecting to ${this.#internal.url}`);
        }
        try {
            this.#nextUrlAndProtocols(closeEvent);
            const c = new this.#internal.WebSocket(this.#url, this.#protocols);
            /* eslint-disable  @typescript-eslint/no-explicit-any */
            (c as any)._internalSocketId = id;
            // do not set it here as onopen may not be called if connection fails
            // this.#internal.socket = c;
            this.#initSocket(c);
        }
        catch(e) {
            if (this.onError) {
                this.onError(e instanceof Error ? e : new Error(`Failed to create socket: ${e}`));
            }
            else {
                logger.warn(`[${id}]: init socket error: ${e}`, e);
            }
        }
    }

    #initSocket(c: WebSocket & { _internalSocketId?: number }) {
        c.onopen = () => {
            logger.info(`[${c._internalSocketId}]: connection to ${this.#internal.url} is up.`);
            const wasConnecting = this.#internal.state === 'connecting';
            if (this.#internal.queue.length > 0) {
                logger.debug(`[${c._internalSocketId}]: flushing ${this.#internal.queue.length} pending messages`);
                while (this.#internal.queue.length > 0) {
                    const data = this.#internal.queue.shift();
                    if (data) {
                        if (wasConnecting) {
                            c.send(data);
                        }
                        else {
                            logger.warn(`[${c._internalSocketId}]: connection to ${this.#internal.url} is ${this.#internal.state} state, dropping message ${data}`);
                        }
                    }
                }
            }
            if (this.#internal.state === 'closing') {
                if (logger.enabledFor('debug')) {
                    logger.debug(`[${c._internalSocketId}]: socket opened but in closing state, closing socket to ${this.#internal.url}`);
                }
                c.close();
                return;
            }
            if (this.#internal.state === '_disconnecting') {
                // connection was disconnected while re-connecting
                if (logger.enabledFor('debug')) {
                    logger.debug(`[${c._internalSocketId}]: socket opened but in disconnecting state, closing socket to ${this.#internal.url}`);
                }
                this.#internal.state = '_initial';
                c.close(1000, WsConnection.#DISCONNECT_REASON);
                return;
            }
            this.#internal.state = 'connected';
            this.#internal.socket = c;
            if (this.onConnect) {
                this.onConnect(c.url, c.protocol);
            }
        }
        c.onerror = (e: Event) => {
            if (this.onError) {
                this.onError(e['error'] as Error);
            }
            else {
                logger.warn(`[${c._internalSocketId}]: got error: ${e}`, e);
            }
        }
        c.onmessage = (msg) => {
            const data = this.#internal.codec.decode(msg.data);
            if (logger.enabledFor('debug')) {
                logger.debug(`[${c._internalSocketId}]: got message ${JSON.stringify(data)} from ${this.#internal.url}`);
            }
            if (this.onReceive) {
                this.onReceive(data);
            }
        };
        c.onclose = (e: CloseEvent) => {
            let wasOpen = false;
            if (this.#internal.socket?._internalSocketId === c._internalSocketId) {
                wasOpen = true;
                this.#internal.socket = undefined;
            }
            if (this.#internal.state === 'closing') {
                if (wasOpen) {
                    if (this.onDisconnect) {
                        this.onDisconnect(e.code, e.reason, e.wasClean);
                    }
                }
                this.#internal.state = 'closed';
                if (logger.enabledFor('info')) {
                    logger.info(`[${c._internalSocketId}]: connection to ${this.#internal.url} has closed (${e.reason}:${e.code}) (wasOpen: ${wasOpen})`);
                }
                if (this.onClose) {
                    this.onClose();
                }
            }
            else {
                const wasConnected = this.#internal.state === 'connected';
                const wasDisconnecting = this.#internal.state === '_disconnecting';
                if (this.#internal.retry === false || e.reason === WsConnection.#DISCONNECT_REASON) {
                    if (logger.enabledFor('debug')) {
                        logger.debug(`[${c._internalSocketId}]: will not reconnect to ${this.#internal.url} (manual disconnect or retry disabled) (wasConnected: ${wasConnected})`);
                    }
                    if (this.#internal.state !== '_initial') {
                        this.#internal.state = '_initial';
                    }
                    if (!wasDisconnecting) {
                        return;
                    }
                }
                else {
                    if (this.#internal.state !== 'connecting') {
                        this.#internal.reconnectAttempt = 0;
                        this.#internal.state = 'connecting';
                    }
                    const reconnectDelay = calculateReconnectDelay(this.#internal.reconnectAttempt, this.#internal.retry);
                    const reconnectAttempt = this.#internal.reconnectAttempt ++;
                    if (logger.enabledFor(`debug`)) {
                        logger.debug(`[${c._internalSocketId}]: schedule reconnect to ${this.#internal.url} in ${reconnectDelay} ms. [attempt: ${reconnectAttempt}]`);
                    }
                    this.#internal.reconnectTimeout = setTimeout(() => {
                        this.#createAndInit(e);
                    }, reconnectDelay);
                }
                if (wasConnected || wasDisconnecting) {
                    logger.info(`[${c._internalSocketId}]: connection to ${this.#internal.url} has disconnected (${e.reason}:${e.code})`);
                    if (this.onDisconnect) {
                        this.onDisconnect(e.code, e.reason, e.wasClean);
                    }
                }
            }
        }
    }

    connect() {
        if (this.#internal.state !== '_initial' && this.#internal.state !== '_disconnecting') {
            return;
        }
        this.#internal.state = 'connecting';

        this.#createAndInit();
    }

    disconnect() {
        if (this.#internal.state === 'closing' || this.#internal.state === 'closed') {
            throw new Error(`connection to ${this.#internal.url} is already closing/closed.`);
        }

        if (this.#internal.state === '_disconnecting' || this.#internal.state === '_initial') {

            return;
        }
        const wasConnecting = this.#internal.state === 'connecting';

        this.#internal.state = '_disconnecting';

        // cancel any pending reconnect
        if (this.#internal.reconnectTimeout) {
            clearTimeout(this.#internal.reconnectTimeout);
            this.#internal.reconnectTimeout = undefined;
            this.#internal.reconnectAttempt = 0;
        }
        const socket = this.#internal.socket;
        if (logger.enabledFor('debug')) {
            logger.debug(`[${socket?._internalSocketId}]: disconnecting connection to ${this.#internal.url} (opened?: ${socket !== undefined})`);
        }
        if (socket) {
            this.#internal.socket = undefined;
            socket.close(1000, WsConnection.#DISCONNECT_REASON);
        } else if (!wasConnecting) {
            this.#internal.state = '_initial';
            if (this.onDisconnect) {
                this.onDisconnect(1000, WsConnection.#DISCONNECT_REASON, true);
            }
        }
    }

    close() {
        if (this.#internal.state === 'closed' || this.#internal.state === 'closing') {
            return;
        }
        const wasConnecting = this.#internal.state === 'connecting';
        this.#internal.state = 'closing';
        // cancel any pending reconnect
        if (this.#internal.reconnectTimeout) {
            clearTimeout(this.#internal.reconnectTimeout);
            this.#internal.reconnectTimeout = undefined;
        }

        const socket = this.#internal.socket;
        if (logger.enabledFor('debug')) {
            logger.debug(`closing connection to ${this.#internal.url} (opened?: ${socket !== undefined})`);
        }

        if (socket) {
            this.#internal.socket = undefined;
            socket.close();
        }
        else if (!wasConnecting) {
            this.#internal.state = 'closed';
            if (this.onClose) {
                this.onClose();
            }
        }
        else {
            // if wasConnecting wait for close/open
        }
    }

    send(msg: T) {
        if (this.#internal.state === 'closing' || this.#internal.state === 'closed') {
            throw new Error(`connection to ${this.#internal.url} is closed.`);
        }
        const socket = this.#internal.socket;
        const enqueue = socket === undefined && this.#internal.maxEnqueued > 0 && this.#internal.queue.length < this.#internal.maxEnqueued;
        if (logger.enabledFor('debug')) {
            let action: 'sending' | 'enqueueing' | 'dropping';
            if (socket !== undefined) {
                action  = 'sending';
            } else if (enqueue) {
                action = 'enqueueing';
            }
            else {
                action = 'dropping';
            }
            logger.debug(`${action} message ${JSON.stringify(msg)} to ${this.#internal.url}`);
        }
        if (socket || enqueue) {
            const data = this.#internal.codec.encode(msg);
            if (socket) {
                socket.send(data);
            } else {
                this.#internal.queue.push(data);
            }
        }
    }

    toJSON() {
        return { url: this.#url, state: this.#internal.state, enqueued: this.#internal.queue.length, attempts: this.#internal.reconnectAttempt };
    }

    #nextUrlAndProtocols(closeEvent?: CloseEvent): void {
        const {url, baseUrl, protocols} = this.#internal.dynamicArguments?.(this.#internal.url, this.#internal.protocols, closeEvent) ?? {};
        const result = new URL(url ?? this.#internal.url, baseUrl);
        this.#url = result.href;
        this.#protocols = protocols ?? [...this.#internal.protocols];
    }
}

function wsConstructor(WebSocketCtor?: typeof WebSocket): (typeof WebSocket) {
    if (WebSocketCtor) {
        return WebSocketCtor;
    }
    if (globalThis.WebSocket) {
        return globalThis.WebSocket;
    }
    else {
        return WebSocket;
    }
}

class WsConnectionFactory<T> implements WebsocketConnectionFactory<T> {
    readonly #codec: GatewayEncoders.Codec<T, string>;
    readonly #retry?: Retry;
    readonly #WebSocket?: typeof WebSocket;
    readonly #connections = new Array<WsConnection<T>>();
    constructor(codec: GatewayEncoders.Codec<T, string>,
                retry?: Retry,
                WebSocketCtor?: typeof WebSocket) {
        this.#codec = codec;
        this.#retry = retry;
        this.#WebSocket = WebSocketCtor;
    }

    create(uri: string | URL, protocols?: string | string[], opts?: WebsocketConnectionOptions): WebsocketConnection<T> {
        const WebSocketCtor = wsConstructor(this.#WebSocket);
        const internal = new InternalConnection<T>(uri, protocols ?? [], {
            codec: this.#codec,
            retry: this.#retry,
            WebSocket: WebSocketCtor,
            ...opts
        });
        const wsc = new WsConnection(internal);
        this.#connections.push(wsc);
        return wsc;
    }

    close(): void {
        while(this.#connections.length > 0) {
            const connection = this.#connections.pop();
            if (connection !== undefined) {
                connection.close();
            }
        }

    }

}

export type Retry = Readonly<{
    min?: number,
    max?: number,
    factor?: number
} | false>


export type WebsocketConnectionFactoryOptions<T extends IOGateway.Message<unknown>> = {
    /**
     * Codec used to encode/decode messages. Defaults to JSON.
     */
    codec?: GatewayEncoders.Codec<T, string>
    /**
     * WebSocket constructor to use.
     * If not specified, the global WebSocket constructor will be used or if not available will try to load it from the `ws` package.
     */
    WebSocket?: typeof WebSocket

    retry?: Retry
}

export function websocketConnectionFactory<T extends IOGateway.Message<unknown>>(opts?: WebsocketConnectionFactoryOptions<T>): WebsocketConnectionFactory<T> {
    const codec = opts?.codec ?? GatewayEncoders.json();
    const retry = opts?.retry;
    const WebSocketCtor = opts?.WebSocket;
    return new WsConnectionFactory<T>(codec, retry, WebSocketCtor);
}
